import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import { getInt } from 'utils/functions';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import {
  COMPUTE_FINANCIAL_PRODUCT_NEXT_STATES,
  FINANCIAL_PRODUCT_QUERY,
  GENERIC_ENTITY_FLOWS_QUERY,
  GENERIC_ENTITY_BUSINESS_STATES_FLOW_QUERY
} from '../../queries';
import { UPDATE_FINANCIAL_PRODUCT_STATE } from '../../mutations';
import { FINANCIAL_PRODUCT_STATE_UPDATED } from '../../subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import { getOidcConfigName } from 'utils/functions';
import { useOidcUser } from '@axa-fr/react-oidc';
import useLanguageTracker from 'hooks/useLanguageTracker';
import { productStateValidator } from '../../../validators';
import { useValidation, isValid, getErrors } from '@totalsoft/pure-validations-react';
import { Autocomplete, IconButton } from '@totalsoft/rocket-ui';
import FlexBox from 'components/common/FlexBox';
import ErrorIcon from '@mui/icons-material/Error';

const FinancialProductStateSelectionContainer = ({ canWrite }) => {
  const { t } = useTranslation();
  const addToast = useToast();
  const [financialProductState, setFinancialProductState] = useState({ id: null, name: '', activeProduct: false });
  const [financialProductFlowId, setFinancialProductFlowId] = useState(null);
  const [financialProductNextStatesLocal, setFinancialProductsNextStatesLocal] = useState([]);
  const [saveDisabled, setSaveDisabled] = useState(true);

  const match = useRouteMatch();
  const productId = match.params.id && getInt(match.params.id);
  const { oidcUser } = useOidcUser(getOidcConfigName());
  const userId = oidcUser?.charisma_user_id;
  const [financialProductStateValidation, validateFinancialProductState] = useValidation(productStateValidator);
  const isConditionValid = isValid(financialProductStateValidation);

  const { data: genericEntityFlowsData, refetch: refetchGenericEntityFlows } = useQuery(GENERIC_ENTITY_FLOWS_QUERY, {
    variables: { context: 'FINANCIAL_PRODUCT' },
    skip: !!productId
  });
  const onLanguageChange = useCallback(() => {
    refetchGenericEntityFlows();
  }, [refetchGenericEntityFlows]);
  useLanguageTracker({ onChange: onLanguageChange });

  const mainFlow = useMemo(
    () => genericEntityFlowsData?.genericEntityFlowsDictionary.find(item => item.mainFlow),
    [genericEntityFlowsData]
  );

  useQuery(GENERIC_ENTITY_BUSINESS_STATES_FLOW_QUERY, {
    variables: { flowId: mainFlow?.id },
    onCompleted: data => {
      const activeState = data?.genericEntityBusinessStatesXFlow?.find(item => item.isInitialState);
      if (activeState) {
        setFinancialProductFlowId(mainFlow?.id);
        setFinancialProductState({
          stateId: activeState.id,
          stateName: activeState.name,
          activeProduct: activeState.activeProduct
        });
      }
    },
    skip: !mainFlow?.id || financialProductState.id
  });

  useQuery(FINANCIAL_PRODUCT_QUERY, {
    variables: { productId },
    skip: !productId,
    onCompleted: data => {
      if (data?.financialProduct?.financialProductXBusinessFlow.flowId) {
        setFinancialProductFlowId(data.financialProduct.financialProductXBusinessFlow.flowId);
        setFinancialProductState({
          stateId: data.financialProduct.financialProductXBusinessFlow.currentStateId,
          stateName: data.financialProduct.financialProductXBusinessFlow.currentStateName,
          activeProduct: data.financialProduct.isActive
        });
      }
    }
  });

  useQuery(COMPUTE_FINANCIAL_PRODUCT_NEXT_STATES, {
    variables: {
      stateId: financialProductState.stateId
    },
    skip: !productId || !financialProductState.stateId,
    onCompleted: data => {
      setSaveDisabled(false);
      setFinancialProductsNextStatesLocal(data.computeFinancialProductNextStates);
    }
  });

  const [financialProductStateSave] = useMutation(UPDATE_FINANCIAL_PRODUCT_STATE, {
    skip: !financialProductState.stateId,
    onError: () => {
      setSaveDisabled(false);
    }
  });

  useSubscription(FINANCIAL_PRODUCT_STATE_UPDATED, {
    skip: !financialProductState.stateId,
    onData: ({ data, client }) => {
      const currentState = financialProductNextStatesLocal.find(x => x.stateId === data.data.financialProductStateUpdated.stateId);
      setSaveDisabled(false);
      setFinancialProductState({
        stateId: currentState?.stateId,
        stateName: currentState?.stateName,
        activeProduct: currentState?.activeProduct
      });
      client.cache.evict({
        id: `FinancialProductItem:{"productId":${data.data.financialProductStateUpdated.productId}}`
      });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedFinancialProducts' });
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: `financialProductState` });
      client.cache.gc();
      addToast(t('OfferEdit.FinancialProducts.StateUpdated'), 'success');
    }
  });

  const handleOnSave = event => {
    const input = { flowId: financialProductFlowId, stateId: event.stateId };
    if (!validateFinancialProductState(input)) return;

    setSaveDisabled(true);
    const nextState = financialProductNextStatesLocal.find(x => x.stateId === event.stateId);
    financialProductStateSave({
      variables: {
        financialProductId: productId,
        newStateId: nextState.stateId,
        newStateName: nextState.stateName,
        flowId: financialProductFlowId,
        userId: parseInt(userId),
        activeProduct: nextState.activeProduct
      }
    });
  };

  useEffect(() => {
    const input = { flowId: financialProductFlowId, stateId: financialProductState.stateId };
    validateFinancialProductState(input);
  }, [financialProductState, financialProductFlowId, validateFinancialProductState]);

  return (
    <FlexBox gap={1} alignItems='center' justifyContent='center'>
      <Autocomplete
        fullWidth
        id={'financialProduct_state'}
        valueKey='stateId'
        labelKey='stateName'
        value={{ stateId: financialProductState.stateId, stateName: financialProductState.stateName }}
        onChange={handleOnSave}
        options={financialProductNextStatesLocal}
        disabled={saveDisabled || !canWrite}
        required
      />
      {!isConditionValid && (
        <IconButton id={'productStateErrorButton'} size='small' color='transparent' tooltip={getErrors(financialProductStateValidation)}>
          <ErrorIcon color='error' />
        </IconButton>
      )}
    </FlexBox>
  );
};
FinancialProductStateSelectionContainer.propTypes = {
  canWrite: PropTypes.bool.isRequired
};

export default FinancialProductStateSelectionContainer;
